import React, { useEffect, useReducer, useState } from 'react'
import { Dropdown } from 'semantic-ui-react'
import styled from 'styled-components'

import CustomerTagSelection from 'components/CustomerTagSelection'
import customerTagCreateUpdateDeletePopupReducer, { initState } from './reducer'
import CustomerTagForm from 'components/CustomerTagForm'
import { message } from 'antd'

/**
 * @typedef {import('components/CustomerTagInput').CustomerTag} CustomerTag
 *
 * @param {object} props
 * @param {CustomerTag[]} props.allTags
 * @param {import('immutable').Set<CustomerTag>} props.defaultSelectedTagSet
 * @param {boolean} [props.loading]
 * @param {function} props.onChange
 * @param {function} props.onAddTag
 * @param {function} props.onRemoveTag
 * @param {function} props.onCreateTag
 * @param {function} props.onModifyTag
 * @param {function} props.onDeleteTag
 */
export default function CustomerTagCreateUpdateDeletePopupContent({
  allTags,
  defaultSelectedTagSet,
  loading,
  onChange,
  onAddTag,
  onRemoveTag,
  onCreateTag,
  onModifyTag,
  onDeleteTag,
}) {
  const [{ state, selectedTagForModification, selectedTagSet }, dispatch] = useReducer(
    customerTagCreateUpdateDeletePopupReducer,
    defaultSelectedTagSet,
    initState
  )

  useEffect(() => {
    dispatch({
      type: 'SET_CUSTOMER_TAG_SET',
      tagSet: defaultSelectedTagSet,
    })
  }, [defaultSelectedTagSet])

  useEffectNotOnMount(() => {
    if (onChange) {
      onChange(selectedTagSet)
    }
  }, [onChange, selectedTagSet])

  function handleClickCreateNewTag() {
    dispatch({
      type: 'CLICK_CREATE_TAG',
    })
  }

  function handleClickModifyTag(tag) {
    dispatch({
      type: 'CLICK_MODIFY_TAG',
      tag,
    })
  }

  function handleClickBack() {
    dispatch({
      type: 'CLICK_BACK',
    })
  }

  function handleModifyTag(newTagName) {
    if (onModifyTag) {
      const isTagExisting = allTags
        .filter((tag) => tag.id !== selectedTagForModification.id)
        .some((tag) => tag.name === newTagName)
      if (isTagExisting) {
        message.error('Whoops! Tag name is already taken.')
        return
      }

      const isTagNameChanged = newTagName !== selectedTagForModification.name
      if (isTagNameChanged) {
        onModifyTag(selectedTagForModification, newTagName)
      }
      handleClickBack()
    }
  }

  function handleDeleteTag() {
    if (onDeleteTag) {
      handleClickBack()
      onDeleteTag(selectedTagForModification)
    }
  }

  function handleCreateTag(tagName) {
    if (onCreateTag) {
      const isTagExisting = allTags.some((tag) => tag.name === tagName)
      if (isTagExisting) {
        message.error('Whoops! Tag name is already taken.')
      } else {
        handleClickBack()
        onCreateTag(tagName)
      }
    }
  }

  /**
   *
   * @param {CustomerTag} tag
   */
  function handleSelectTag(tag) {
    const matchedTag = selectedTagSet.toArray().find(({ name }) => name === tag.name)
    if (matchedTag) {
      dispatch({
        type: 'REMOVE_TAG',
        tag: matchedTag,
      })
      onRemoveTag(tag)
    } else {
      dispatch({
        type: 'ADD_TAG',
        tag,
      })
      onAddTag(tag)
    }
  }

  if (state === 'tagSelection') {
    return (
      <CustomDropdown className="visible ui dropdown active floating fluid">
        <Dropdown.Menu className="visible">
          <CustomerTagSelection
            loading={loading}
            allTags={allTags}
            selectedTagSet={selectedTagSet}
            onSelectTag={handleSelectTag}
            onClickCreateNewTag={handleClickCreateNewTag}
            onClickModifyTag={handleClickModifyTag}
          />
        </Dropdown.Menu>
      </CustomDropdown>
    )
  }

  if (state === 'tagCreation') {
    return (
      <CustomDropdown className="visible ui dropdown active floating fluid">
        <Dropdown.Menu className="visible">
          <CustomerTagForm mode="create" onClickBack={handleClickBack} onSubmit={handleCreateTag} />
        </Dropdown.Menu>
      </CustomDropdown>
    )
  }

  if (state === 'tagModification') {
    return (
      <CustomDropdown className="visible ui dropdown active floating fluid">
        <Dropdown.Menu className="visible">
          <CustomerTagForm
            mode="modify"
            tag={selectedTagForModification}
            onClickBack={handleClickBack}
            onSubmit={handleModifyTag}
            onDelete={handleDeleteTag}
          />
        </Dropdown.Menu>
      </CustomDropdown>
    )
  }

  return null
}

function useEffectNotOnMount(callback, dependencies) {
  const [isMounted, setIsMounted] = useState(false)
  useEffect(() => {
    if (isMounted) {
      callback()
    } else {
      setIsMounted(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, dependencies)
}

const CustomDropdown = styled.div`
  &.ui.dropdown {
    width: inherit;
  }

  &.ui.dropdown .menu {
    min-width: unset;
    position: relative;
    border: 0;
    box-shadow: none !important;
    margin-top: 0 !important;
  }

  &.ui.dropdown .header {
    margin: 0;
    text-transform: none;
  }

  &.ui.dropdown .menu .scrolling.menu {
    border: 0;
    box-shadow: none !important;
    max-height: 140px;
  }

  &.ui.dropdown .ui.empty.circular.label {
    padding: 6px !important;
    margin-right: 8px;
  }

  &.ui.dropdown .menu > .item.create-tag {
    text-align: center;
    font-weight: bold;

    &:hover {
      background-color: white;
    }
  }

  &.ui.dropdown .ui.form {
    padding: 10px;
  }
`



// WEBPACK FOOTER //
// ./node_modules/eslint-loader??ref--0-0!./src/organisms/CustomerTagCreateUpdateDeletePopupContent/index.js